<template>
	<div class="budget-health-charts">
		<div ref="detectStickyHeaderEl" />
		<header
			class="budget-health-charts__header"
			:class="{
				'budget-health-charts__header--scrolling':
					hasStickyHeaderShadow,
			}"
		>
			<div class="budget-health-charts__header-heading">
				<h3 class="budget-healt-charts__header-title">
					{{ $t('project.tab.budgethealth') }}
				</h3>
				<SuiInfoButton as="a" :href="REPORTS_HELP_ARTICLE_URL">
					{{ $t('project_learn_more') }}
				</SuiInfoButton>
			</div>
			<SuiMultiToggle
				v-if="canSeeProjectBudgetMonetary"
				v-slot="{ item }"
				v-model="selectedReportType"
				:items="projectToggleOptions"
				:with-gaps="false"
				:can-unselect="false"
				class="budget-health-charts__header-toggle"
			>
				<SuiMultiToggleButton :value="item.value">
					<template v-if="item.icon" #left-content>
						<SuiIcon :name="item.icon" />
					</template>
					{{ item.content }}
				</SuiMultiToggleButton>
			</SuiMultiToggle>
		</header>

		<div class="budget-health-charts__charts-container">
			<BudgetChartRevenue
				v-if="selectedReportType === BudgetHealthReportTypes.REVENUE"
				:project-id="projectId"
				:retainer-period-id="retainerPeriodId"
				is-viewed-in-grid
			/>
			<BudgetChart
				v-else
				:project-id="projectId"
				:retainer-period-id="retainerPeriodId"
				is-viewed-in-grid
			/>
			<BurnUpChartRevenue
				v-if="selectedReportType === BudgetHealthReportTypes.REVENUE"
				:project-id="projectId"
				:retainer-period-id="retainerPeriodId"
				is-viewed-in-grid
			/>
			<BurnUpChartTime
				v-else
				:project-id="projectId"
				:retainer-period-id="retainerPeriodId"
				is-viewed-in-grid
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import {
	SuiMultiToggle,
	SuiMultiToggleButton,
	SuiIcon,
	SuiInfoButton,
} from '@developers/scoro-ui';
import {
	BurnUpChartTime,
	BurnUpChartRevenue,
	BudgetChart,
	BudgetChartRevenue,
} from '@/features/Project';
import { computed, onMounted, ref } from 'vue';
import { REPORTS_HELP_ARTICLE_URL } from '../constants';
import { usePermissions, useTranslation } from '@/composables';
import { BudgetHealthReportTypes } from './types';
import { Permission, PermissionAction } from '@/constants/permissions';
import { type RetainerPeriod, type Project } from '@/generated/graphql';
import { Feature } from '@/constants/features';

export interface BudgetHealthChartsProps {
	projectId: Project['id'];
	retainerPeriodId?: RetainerPeriod['id'];
}

defineProps<BudgetHealthChartsProps>();

const { t } = useTranslation();
const { can } = usePermissions();
const { userHas } = usePermissions();

const detectStickyHeaderEl = ref<HTMLDivElement | null>(null);
const hasStickyHeaderShadow = ref(false);

const projectToggleOptions = [
	{
		value: BudgetHealthReportTypes.REVENUE,
		icon: 'icon-paper-coins-16',
		content: t('budget_health_budget_type_revenue'),
	},
	{
		value: BudgetHealthReportTypes.TIME,
		icon: 'icon-clock-16',
		content: t('budget_health_budget_type_time'),
	},
];

const canSeeProjectBudgetMonetary = computed(
	() =>
		(userHas(Feature.PROJECT_ADDONS) ||
			userHas(Feature.PROJECT_BUDGET_MONETARY)) &&
		can(
			PermissionAction.HAS_PERMISSION,
			Permission.CAN_SEE_PROJECT_INCOME_COST
		)
);

const selectedReportType = ref(
	canSeeProjectBudgetMonetary.value
		? BudgetHealthReportTypes.REVENUE
		: BudgetHealthReportTypes.TIME
);

const isIntersecting = (entries: IntersectionObserverEntry[]) =>
	entries.some((entry) => entry.isIntersecting);

const stickyHeaderObserver = new IntersectionObserver(
	(entries) => {
		hasStickyHeaderShadow.value = !isIntersecting(entries);
	},
	{
		rootMargin: '-60px 0px 0px 0px',
		threshold: 1,
	}
);

onMounted(() => {
	if (detectStickyHeaderEl.value) {
		stickyHeaderObserver.observe(detectStickyHeaderEl.value);
	}
});
</script>

<style lang="scss" scoped>
.budget-health-charts {
	margin-top: 1rem;

	--shadow-overflow-margin: 0.625rem;

	&__header {
		padding: 3rem 6rem 2rem;
		margin: -3rem -6rem 0;
		background-color: $neutral-00;
		z-index: z('base');
		display: flex;
		justify-content: space-between;

		&--scrolling {
			position: sticky;
			top: 7.25rem;
			box-shadow: $scoro-box-shadow;
		}
	}

	&__header-title {
		padding: 1.125rem 0 1rem;
	}

	&__header-heading {
		display: flex;
		gap: 2rem;
		align-items: center;
	}

	&__header-toggle {
		max-width: 25rem;
	}

	&__charts-container {
		display: flex;
		gap: 2rem;
		flex-direction: column-reverse;
		margin: 2rem 0 0;
	}
}
</style>
